import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DigitalTwin from './DigitalTwin'
import EventLog from './EventLog'
import RemoteAccessLogin from '../RemoteAccessLogin'
import RemoteAccessSession from '../RemoteAccessSession'
import Breadcrumb from '../common/Breadcrumb'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import WrappedTabs from '../common/WrappedTabs'
import SimpleButton from '../common/SimpleButton'
import TooltipedIcon from '../common/TooltipedIcon'
import { toast } from '../common/Toast'
import { fetchLatest } from '../../utils/requests/telemetryAPI'
import { endRemoteSession, startRemoteSession, updateSessionEndDate } from '../../utils/requests/remoteAccess'
import { REMOTE_SESSION_TIME, REMOTE_SESSION_DATA } from '../../actions/root'
import { hasAdvancedTroubleshootingPermission, hasColteneSiteAccess, hasEnhancedEventLogPermission } from '../../utils/permissionValidation'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import { connect } from 'react-redux'
import { publishClearRemoteSession } from '../../utils/requests/mqttPublishAPI'
import { InformationIcon } from '../../icons'
import './remoteAccess.scss'

const refreshInterval = 3 * 1000

function RemoteAccess(props) {
  const { t } = useTranslation()
  const { setRemoteSession } = props
  const history = useHistory()
  const location = useLocation()
  const [currentRemoteSession, setCurrentRemoteSession] = useState(JSON.parse(localStorage.getItem(REMOTE_SESSION_DATA)))
  const [remoteSessionTime, setRemoteSessionTime] = useState(localStorage.getItem(REMOTE_SESSION_TIME))
  const [rawData, setRawData] = useState([])
  const [rawHistoricInput, setRawHistoricInput] = useState([])
  const [rawHistoricOutput, setRawHistoricOutput] = useState([])
  const [chartEndTime, setChartEndTime] = useState(null)
  const [serNum, setSerNum] = useState(null)
  const [productIsG4Plus, setProductIsG4Plus] = useState(false)
  const [elapsedTime, setElapsedTime] = useState('-- : --')
  const [tabs, setTabs] = useState([])
  const [activeTab, setActiveTab] = useState(null)
  const [isBackwards, setIsBackwards] = useState(false)
  const [isLatest, setIsLatest] = useState(true)
  const [isForwards, setIsForwards] = useState(false)
  const deviceModel = localStorage.getItem('deviceModel')
  const hasGuid = currentRemoteSession?.guid
  const hasColteneSitePermission = hasColteneSiteAccess(props.userPermissions)

  useEffect(() => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.remoteAccessPage,
      label: 'Remote Access Page: Page View',
    })
  }, [])

  const handleCloseSession = useCallback(() => {
    if (hasGuid) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.remoteAccessPage,
        label: `Remote Access Page: Close Session - ${serNum || 'Unknown Device'}`,
      })

      const data = {
        guid: currentRemoteSession.guid,
        ip: currentRemoteSession.ip || '127.0.0.1',
      }

      endRemoteSession(data)
        .then(() => {
          setRemoteSession(false)
          setCurrentRemoteSession(null)
          localStorage.removeItem(REMOTE_SESSION_TIME)
          localStorage.removeItem(REMOTE_SESSION_DATA)
          localStorage.removeItem('activeSessionToken')
          localStorage.removeItem('deviceModel')
          toast.success(t('remote_access_context.session_closed'))
          setSerNum(null)
          setProductIsG4Plus(false)
          setRawHistoricInput([])
          setRawHistoricOutput([])
          setRawData([])
        })
        .catch((error) => {
          setRemoteSession(false)
          setCurrentRemoteSession(null)
          localStorage.removeItem(REMOTE_SESSION_TIME)
          localStorage.removeItem(REMOTE_SESSION_DATA)
          localStorage.removeItem('activeSessionToken')
          localStorage.removeItem('deviceModel')
          toast.error(t('remote_access_context.session_closed'))
          setSerNum(null)
          setProductIsG4Plus(false)
          setRawHistoricInput([])
          setRawHistoricOutput([])
          setRawData([])
          console.log(error.message)
        })

      const payload = {
        serial_number: serNum,
        session_status: 'complete',
        date: new Date().toISOString(), // iso datetime
      }

      publishClearRemoteSession(serNum, payload)

      const bodyData = {
        serial_number: serNum,
        datetime: new Date().toISOString(),
        email: props.authedUser?.email,
        guid: currentRemoteSession.guid,
        token: JSON.parse(localStorage.getItem('activeSessionToken'))?.token,
      }

      updateSessionEndDate(bodyData)
        .then(() => {})
        .catch((error) => {
          toast.error(t('error.error_updating_web_session_date'))
        })
    }
  }, [hasGuid, currentRemoteSession?.ip, serNum, setRemoteSession])

  useEffect(() => {
    const remoteAccessPollingInterval = (process.env.REACT_APP_REMOTE_ACCESS_FREQUENCY_SECONDS || 30) / 60
    const intervalId = setInterval(() => {
      const activeSessionToken = JSON.parse(localStorage.getItem('activeSessionToken'))
      if (activeSessionToken) {
        startRemoteSession(props.authedUser?.cognitoSub, activeSessionToken).then((data) => {
          if (!data.data.guid) {
            handleCloseSession()
          }
        })
      }
    }, 60000 * remoteAccessPollingInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [handleCloseSession, props.authedUser?.cognitoSub])

  useLayoutEffect(() => {
    if (currentRemoteSession && Object.keys(currentRemoteSession).length > 0) {
      setRemoteSessionTime(localStorage.getItem(REMOTE_SESSION_TIME) || 0)
      setSerNum(currentRemoteSession.serial_number)
      setProductIsG4Plus(currentRemoteSession.is_g4_plus)
    }
  }, [currentRemoteSession])

  useLayoutEffect(() => {
    const tabData = [
      {
        key: 0,
        slug: 'remote-access-login',
        title: 'remote_access_context.remote_access_login',
        content: (
          <RemoteAccessLogin
            setRemoteSession={setRemoteSession}
            currentRemoteSession={currentRemoteSession}
            setCurrentRemoteSession={setCurrentRemoteSession}
            disableRemoteSession={() => handleCloseSession()}
          />
        ),
      },
      {
        key: 1,
        slug: 'remote-access-session',
        title: 'remote_access_session',
        content: <RemoteAccessSession disableRemoteSession={() => handleCloseSession()} />,
      },
      hasAdvancedTroubleshootingPermission(props.userPermissions) && {
        key: 2,
        slug: 'advanced-troubleshooting',
        title: 'advanced_troubleshooting',
        content: (
          <DigitalTwin
            rawData={rawData}
            rawHistoricInput={rawHistoricInput}
            rawHistoricOutput={rawHistoricOutput}
            endTime={chartEndTime}
            serNum={serNum}
            setEndTime={setChartEndTime}
            setRawHistoricInput={setRawHistoricInput}
            setRawHistoricOutput={setRawHistoricOutput}
            isBackwards={isBackwards}
            setIsBackwards={setIsBackwards}
            isLatest={isLatest}
            setIsLatest={setIsLatest}
            isForwards={isForwards}
            setIsForwards={setIsForwards}
            deviceModel={deviceModel}
          />
        ),
      },
      hasEnhancedEventLogPermission(props.userPermissions) && {
        key: 3,
        slug: 'event-log',
        title: 'enhanced_event_log.title',
        content: <EventLog serialNumber={serNum} />,
      },
    ].filter(Boolean)

    let filteredTabs
    if (!hasGuid) {
      filteredTabs = tabData.filter((tab) => tab.key === 0)
    } else if (productIsG4Plus) {
      filteredTabs = tabData.filter((tab) => tab.key !== 0)
    } else {
      filteredTabs = tabData.filter((tab) => tab.key === 1)
    }
    setTabs(filteredTabs)

    setActiveTab((prev) => {
      const match = prev ? filteredTabs.find((t) => t.key === prev.key) : null
      return match || filteredTabs[0] || null
    })
  }, [
    hasGuid,
    productIsG4Plus,
    rawData,
    rawHistoricInput,
    rawHistoricOutput,
    chartEndTime,
    isBackwards,
    isLatest,
    isForwards,
    deviceModel,
    props.userPermissions,
    setRemoteSession,
    handleCloseSession,
    currentRemoteSession,
    serNum,
  ])
  useEffect(() => {
    if (!tabs.length) {
      setActiveTab(null)
      return
    }

    const params = new URLSearchParams(location.search)
    const contentParam = params.get('content')
    let selected = tabs[0]

    if (contentParam) {
      const match = tabs.find((t) => t.slug === contentParam)
      if (match) selected = match
    }
    setActiveTab(selected)
    if (params.get('content') !== selected.slug) {
      params.set('content', selected.slug)
      history.replace({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
    }
  }, [location.search, location.pathname, location.state, history, tabs])
  useEffect(() => {
    const timer = setInterval(() => {
      if (currentRemoteSession && Object.keys(currentRemoteSession).length > 0 && remoteSessionTime && remoteSessionTime > 0) {
        const currentTime = new Date().getTime()
        let diff = Math.floor((Number(remoteSessionTime) - currentTime) / 1000)

        if (diff < 0) {
          diff = 0
        }

        let h = Math.floor(diff / 3600)
        diff -= h * 3600
        let m = Math.floor(diff / 60)
        diff -= m * 60
        let s = diff
        setElapsedTime(`${h ? h + ' : ' : ''}${(m < 10 ? '0' : '') + m} : ${(s < 10 ? '0' : '') + s}`)
      }
    }, 500)

    return () => {
      clearInterval(timer)
    }
  }, [currentRemoteSession, remoteSessionTime])

  const getTelemetryData = useCallback((serNum) => {
    fetchLatest(serNum)
      .then((data) => {
        setRawData(data)
        setRawHistoricInput((prevRawHistoricInput) => [...prevRawHistoricInput, data])
        setRawHistoricOutput((prevRawHistoricOutput) => [...prevRawHistoricOutput, data])
      })
      .catch((e) => {
        console.log('ERROR', e.message)
      })
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (serNum && productIsG4Plus && isLatest) {
        getTelemetryData(serNum)
      }
    }, refreshInterval)

    return () => clearInterval(intervalId)
  }, [getTelemetryData, serNum, productIsG4Plus, isLatest])

  useEffect(() => {
    if (serNum) {
      const endTime = new Date()
      setChartEndTime(endTime)
    }
  }, [serNum])

  const handleChangeTab = (tabKey) => {
    const tab = tabs.find((tab) => tab.key === tabKey)
    if (!tab) return
    const tabNames = {
      0: 'Remote Access Login',
      1: 'Remote Access Session',
      2: 'Advanced Troubleshooting',
      3: 'Event Log',
    }

    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.remoteAccessPage,
      label: `Remote Access Page: Tab Change - ${tabNames[tabKey] || 'Unknown Tab'}`,
    })

    setActiveTab(tab)
    const params = new URLSearchParams(location.search)
    params.set('content', tab.slug)
    history.push({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
  }

  const getMaxSessionDuration = () => {
    const sessionDurationMinutes = Number(process.env.REACT_APP_REMOTE_SESSION_DURATION_MINUTES || 120)

    if (sessionDurationMinutes > 59) {
      const hours = Math.floor(sessionDurationMinutes / 60)
      const minutes = sessionDurationMinutes % 60

      let durationText = `${hours} ${t('hours')} `

      if (minutes > 0) {
        durationText += `${minutes} ${t('minutes')}`
      }

      return durationText.trimEnd()
    }

    return `${sessionDurationMinutes} ${t('minutes')}`
  }

  return (
    <div className="remote-access-wrapper">
      <Breadcrumb path={location.pathname} />
      <DashboardHeader
        headerText={serNum || t('nav.remote_access')}
        children={
          hasColteneSitePermission && <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('remote_access_context.device_info')}`} orientation="right" />
        }
      />
      {hasGuid && (
        <div className="active-remote-session-wrapper">
          <div className="timer-wrapper">
            <div className="timer-title">
              {t('remote_access_context.session_countdown')}
              <div className="timer-note">
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('remote_access_context.session_duration')}: ${getMaxSessionDuration()}`} />
              </div>
            </div>
            <div className="timer-elapsed">{elapsedTime}</div>
          </div>
          <SimpleButton className="submit-button" onClick={() => handleCloseSession()}>
            {t('remote_access_context.close_session')}
          </SimpleButton>
        </div>
      )}
      <WrappedTabs tabs={tabs.map((tab) => ({ id: tab.key, name: tab.title }))} activeTab={activeTab?.key} changeTab={handleChangeTab} />
      {activeTab?.content && activeTab.content}
    </div>
  )
}

function mapStateToProps({ authedUser, userPermissions }) {
  return {
    authedUser,
    userPermissions: userPermissions || {},
  }
}

export default connect(mapStateToProps)(RemoteAccess)

